<template>
  <section class="about-page">
    <section class="about-hero">
      <div class="about-shell hero-inner">
        <p class="hero-eyebrow">Community flyers, local reach</p>
        <h1 class="hero-title">
          Discover<br />
          <span>NetFlyr</span>
        </h1>
        <p class="hero-copy">
          Whether you're looking for roommates, rides, jobs, services, events, or local offers, NetFlyr makes it easier to post and discover community flyers that matter nearby.
        </p>
        <div class="hero-actions">
          <router-link class="nf-primary-button" to="/create">
            <span class="hero-button-icon">+</span>
            <span>Post a Flyer</span>
          </router-link>
          <router-link class="hero-button hero-button-secondary" to="/explore">
            <span>Explore Flyers</span>
          </router-link>
        </div>
      </div>
    </section>

    <section class="about-section">
      <div class="about-shell">
        <SectionHeading title="Why NetFlyr?" />
        <div class="feature-grid feature-grid-3">
          <article v-for="item in whyItems" :key="item.title" class="info-card">
            <div class="icon-chip icon-chip-soft" :style="{ color: item.color }" v-html="item.icon"></div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="about-section">
      <div class="about-shell">
        <SectionHeading title="What You Can Post" />
        <div class="feature-grid feature-grid-2">
          <article v-for="item in postItems" :key="item.title" class="info-card info-card-centered">
            <div class="icon-chip icon-chip-solid" :style="{ background: item.background }" v-html="item.icon"></div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="about-section">
      <div class="about-shell">
        <SectionHeading title="How It Works" />
        <div class="workflow-head">
          <div class="workflow-label">
            <span class="workflow-icon workflow-icon-post">+</span>
            <h3>For Posting</h3>
          </div>
          <div class="workflow-label">
            <span class="workflow-icon workflow-icon-browse" v-html="browseIcon"></span>
            <h3>For Browsing</h3>
          </div>
        </div>
        <div class="workflow-grid">
          <div class="workflow-column">
            <article v-for="item in postingSteps" :key="item.step" class="step-card">
              <span class="step-badge step-badge-post">{{ item.step }}</span>
              <div>
                <h4>{{ item.title }}</h4>
                <p>{{ item.description }}</p>
              </div>
            </article>
          </div>
          <div class="workflow-column">
            <article v-for="item in browsingSteps" :key="item.step" class="step-card">
              <span class="step-badge step-badge-browse">{{ item.step }}</span>
              <div>
                <h4>{{ item.title }}</h4>
                <p>{{ item.description }}</p>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>

    <section class="about-section">
      <div class="about-shell">
        <SectionHeading title="Who It Helps" />
        <div class="feature-grid feature-grid-3">
          <article v-for="item in helpItems" :key="item.title" class="info-card info-card-centered compact-card">
            <div class="icon-chip icon-chip-soft" :style="{ color: item.color }" v-html="item.icon"></div>
            <p class="compact-text">{{ item.title }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="about-section">
      <div class="about-shell text-shell">
        <SectionHeading title="Your Local Community Platform" />
        <div class="long-copy">
          <p>
            NetFlyr is a local community flyer platform built to help people share what matters nearby without extra noise. It is designed for practical everyday needs: finding a roommate, sharing a ride, posting a job, promoting a service, or getting the word out about an event or offer.
          </p>
          <p>
            Users can post visual flyers quickly, set a target audience location, choose the right content type, and get their flyer in front of the people most likely to care. On the other side, browsers can explore nearby flyers, filter by category, and connect directly when something is relevant.
          </p>
          <p>
            From renting out a room to promoting a weekend special, NetFlyr keeps local discovery simple. The goal is direct community reach, cleaner browsing, and a posting flow that works on mobile, tablet, and desktop without friction.
          </p>
          <p>
            We do not replace communities. We support them with a cleaner way to publish and discover flyers that stay local, useful, and easy to act on.
          </p>
        </div>
      </div>
    </section>

    <section class="about-section contact-band">
      <div class="about-shell">
        <SectionHeading title="Contact Us" />
        <div class="contact-card">
          <div class="contact-icon" v-html="chatIcon"></div>
          <p>Have questions or suggestions? We'd love to hear from you.</p>
          <p class="contact-email">Email: <a href="mailto:support@netflyr.com">support@netflyr.com</a></p>
        </div>
        <div class="contact-actions">
          <router-link class="nf-primary-button" to="/explore">
            <span>Explore Flyers</span>
          </router-link>
        </div>
      </div>
    </section>

    <section class="about-footer">
      <div class="about-shell footer-inner">
        <div class="social-row">
          <a v-for="item in socialLinks" :key="item.label" class="social-link" :href="item.href" target="_blank" rel="noreferrer" :aria-label="item.label" v-html="item.icon"></a>
        </div>
        <div class="cities">
          <h4>Top Cities</h4>
          <p>{{ topCities.join(' · ') }}</p>
        </div>
        <div class="footer-links">
          <router-link to="/privacy-policy">Privacy Policy</router-link>
          <router-link to="/terms-conditions">Terms & Conditions</router-link>
          <span>NetFlyr © 2026. All rights reserved.</span>
        </div>
      </div>
    </section>
  </section>
</template>

<script setup>
import { defineComponent, h } from 'vue'

const locationIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>'
const boltIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z"/></svg>'
const clockIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>'
const chatIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/></svg>'
const globeIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/></svg>'
const shieldIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3 5 6v6c0 5 3.5 8 7 9 3.5-1 7-4 7-9V6l-7-3Z"/></svg>'
const cartIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="20" r="1.5"/><circle cx="18" cy="20" r="1.5"/><path d="M3 4h2l2.4 10.2a1 1 0 0 0 1 .8h9.8a1 1 0 0 0 1-.8L21 7H7"/></svg>'
const rideIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 16l1.5-5A2 2 0 0 1 8.4 9h7.2a2 2 0 0 1 1.9 2L19 16"/><path d="M4 16h16"/><circle cx="7" cy="18" r="1.5"/><circle cx="17" cy="18" r="1.5"/></svg>'
const homeIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V21h14V9.5"/><path d="M9 21v-6h6v6"/></svg>'
const briefcaseIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>'
const graduationIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m2 9 10-5 10 5-10 5Z"/><path d="M6 11v4c0 1.7 2.7 3 6 3s6-1.3 6-3v-4"/></svg>'
const buildingIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 21V7a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v14"/><path d="M2 21h20"/><path d="M8 9h2M8 13h2M8 17h2M12 9h2M12 13h2M12 17h2"/></svg>'
const usersIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2"/><circle cx="9.5" cy="7" r="3.5"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a3.5 3.5 0 0 1 0 6.74"/></svg>'
const heartIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 20-1.4-1.3C5 13.6 2 10.8 2 7.5 2 5 4 3 6.5 3c1.7 0 3.4.8 4.5 2.1C12.1 3.8 13.8 3 15.5 3 18 3 20 5 20 7.5c0 3.3-3 6.1-8.6 11.2Z"/></svg>'
const browseIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>'
const xIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round"><path d="M4 4 20 20"/><path d="M20 4 4 20"/></svg>'
const facebookIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h-2a4 4 0 0 0-4 4v3H6v4h3v7h4v-7h3l1-4h-4V7a1 1 0 0 1 1-1h3"/></svg>'
const youtubeIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12s0-3-1-4-4-1-9-1-8 0-9 1-1 4-1 4 0 3 1 4 4 1 9 1 8 0 9-1 1-4 1-4Z"/><path d="m10 9 5 3-5 3Z"/></svg>'
const instagramIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><path d="M17.5 6.5h.01"/></svg>'

const whyItems = [
  { title: 'Lightning Fast', description: 'Post a flyer in under a minute with a mobile-first flow.', icon: boltIcon, color: '#f97316' },
  { title: 'Location Smart', description: 'Choose a place, target a region, and keep discovery local.', icon: locationIcon, color: '#f97316' },
  { title: 'Instant Live', description: 'Flyers can go live quickly so time-sensitive posts move fast.', icon: clockIcon, color: '#f97316' },
  { title: 'Built-in Chat', description: 'Connect directly once someone finds a flyer that matters.', icon: chatIcon, color: '#f97316' },
  { title: 'Wide Reach', description: 'Target nearby users or broaden visibility when the post needs it.', icon: globeIcon, color: '#f97316' },
  { title: 'Community First', description: 'Built for practical local discovery and community sharing.', icon: shieldIcon, color: '#f97316' }
]

const postItems = [
  { title: 'Marketplace', description: 'Buy, sell, promote services, and share local deals.', icon: cartIcon, background: 'linear-gradient(135deg, #22c55e, #10b981)' },
  { title: 'Rides', description: 'Post or find rides between neighborhoods, cities, or airports.', icon: rideIcon, background: 'linear-gradient(135deg, #3b82f6, #06b6d4)' },
  { title: 'Accommodations', description: 'Share rooms, rentals, subleases, and roommate needs.', icon: homeIcon, background: 'linear-gradient(135deg, #a855f7, #ec4899)' },
  { title: 'Jobs', description: 'Post openings, gigs, referrals, and hiring updates.', icon: briefcaseIcon, background: 'linear-gradient(135deg, #f97316, #ef4444)' }
]

const postingSteps = [
  { step: 1, title: 'Select & Fill', description: 'Choose the right category and add your flyer details.' },
  { step: 2, title: 'Set Location', description: 'Pick the flyer origin and define your target audience.' },
  { step: 3, title: 'Go Live', description: 'Publish and let nearby users discover it immediately.' }
]

const browsingSteps = [
  { step: 1, title: 'Search Smart', description: 'Browse by location, content type, or what matters nearby.' },
  { step: 2, title: 'Filter Better', description: 'Narrow results by local relevance and category intent.' },
  { step: 3, title: 'Connect Direct', description: 'Reach out when a flyer matches what you need.' }
]

const helpItems = [
  { title: 'Students looking for roommates', icon: graduationIcon, color: '#9333ea' },
  { title: 'Professionals relocating or job hunting', icon: buildingIcon, color: '#2563eb' },
  { title: 'Families offering or finding local help', icon: usersIcon, color: '#16a34a' },
  { title: 'Ride-sharers splitting miles', icon: rideIcon, color: '#ea580c' },
  { title: 'Small businesses promoting services', icon: briefcaseIcon, color: '#dc2626' },
  { title: 'Anyone who prefers local-first discovery', icon: heartIcon, color: '#db2777' }
]

const socialLinks = [
  { label: 'X', href: '#', icon: xIcon },
  { label: 'Facebook', href: '#', icon: facebookIcon },
  { label: 'YouTube', href: '#', icon: youtubeIcon },
  { label: 'Instagram', href: '#', icon: instagramIcon }
]

const topCities = ['Fremont', 'San Jose', 'Dallas', 'Houston', 'Jersey City', 'Tampa', 'Austin', 'Overland Park', 'Edison']

const SectionHeading = defineComponent({
  props: {
    title: { type: String, required: true }
  },
  setup(props) {
    return () =>
      h('div', { class: 'section-heading' }, [
        h('h2', props.title),
        h('span', { class: 'heading-underline', 'aria-hidden': 'true' })
      ])
  }
})
</script>

<style scoped>
.about-page {
  background: #f8fafc;
  color: #0f172a;
}

.about-shell {
  margin: 0 auto;
  width: 100%;
  max-width: 78rem;
  padding: 0 1.25rem;
}

.about-hero {
  position: relative;
  overflow: hidden;
  background: linear-gradient(140deg, #2563eb 0%, #4338ca 45%, #5b21b6 100%);
  color: white;
}

.about-hero::before,
.about-hero::after {
  position: absolute;
  inset: auto;
  content: '';
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.08);
  filter: blur(2px);
}

.about-hero::before {
  left: -10%;
  bottom: 18%;
  width: 70%;
  height: 18rem;
}

.about-hero::after {
  right: -8%;
  bottom: -6rem;
  width: 68%;
  height: 22rem;
}

.hero-inner {
  position: relative;
  z-index: 1;
  display: grid;
  justify-items: center;
  padding-top: 5.5rem;
  padding-bottom: 6rem;
  text-align: center;
}

.hero-eyebrow {
  margin: 0 0 1rem;
  font-size: 0.85rem;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.78);
}

.hero-title {
  margin: 0;
  font-size: clamp(3.5rem, 9vw, 6.5rem);
  font-weight: 900;
  line-height: 0.95;
}

.hero-title span {
  background: linear-gradient(90deg, #dbeafe 0%, #c4b5fd 55%, #e9d5ff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.hero-copy {
  margin: 2rem auto 0;
  max-width: 52rem;
  font-size: clamp(1.05rem, 2vw, 1.6rem);
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.96);
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
}

.hero-button {
  display: inline-flex;
  min-height: 3.75rem;
  min-width: 13rem;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  border-radius: 9999px;
  padding: 0 1.6rem;
  font-size: 1.05rem;
  font-weight: 800;
  text-decoration: none;
}

.hero-button-primary {
  background: #2563eb;
  color: white;
  box-shadow: 0 20px 45px rgba(37, 99, 235, 0.28);
}

.hero-button-secondary {
  border: 1px solid rgba(255, 255, 255, 0.55);
  color: white;
  background: rgba(255, 255, 255, 0.06);
}

.hero-button-icon {
  font-size: 1.35rem;
  line-height: 1;
}

.about-section {
  padding: 5.5rem 0;
}

.contact-band,
.about-footer {
  background:
    radial-gradient(circle at top left, rgba(255, 115, 0, 0.08), transparent 32%),
    radial-gradient(circle at top right, rgba(249, 115, 22, 0.08), transparent 28%),
    #fff;
}

.section-heading {
  display: grid;
  justify-items: center;
  margin-bottom: 3rem;
  text-align: center;
}

.section-heading h2 {
  margin: 0;
  font-size: clamp(2.4rem, 6vw, 4.5rem);
  font-weight: 900;
  line-height: 1.03;
}

.heading-underline {
  width: 8rem;
  height: 0.25rem;
  margin-top: 1rem;
  border-radius: 9999px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #4338ca 48%, rgba(255, 255, 255, 0) 100%);
}

.feature-grid {
  display: grid;
  gap: 1.5rem;
}

.feature-grid-3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.feature-grid-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.info-card,
.step-card,
.contact-card {
  border: 1px solid #e7edf5;
  border-radius: 1.75rem;
  background: white;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.05);
}

.info-card {
  display: grid;
  align-content: start;
  gap: 1.25rem;
  min-height: 19rem;
  padding: 2rem;
}

.info-card-centered {
  justify-items: center;
  text-align: center;
}

.compact-card {
  min-height: 15rem;
}

.info-card h3 {
  margin: 0;
  font-size: 2rem;
  line-height: 1.2;
}

.info-card p,
.step-card p,
.long-copy p,
.contact-card p,
.cities p,
.footer-links,
.compact-text {
  margin: 0;
  font-size: 1rem;
  line-height: 1.65;
  color: #64748b;
}

.compact-text {
  font-size: 1.05rem;
  color: #0f172a;
}

.icon-chip {
  display: grid;
  height: 4rem;
  width: 4rem;
  place-items: center;
  border-radius: 1.25rem;
}

.icon-chip :deep(svg) {
  width: 1.8rem;
  height: 1.8rem;
}

.icon-chip-soft {
  background: #eef2ff;
}

.icon-chip-solid {
  color: white;
}

.workflow-head {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2rem;
  justify-items: center;
  margin-bottom: 2rem;
}

.workflow-label {
  display: grid;
  justify-items: center;
  gap: 1rem;
  text-align: center;
}

.workflow-label h3 {
  margin: 0;
  font-size: 2.1rem;
}

.workflow-icon {
  display: grid;
  height: 4rem;
  width: 4rem;
  place-items: center;
  border-radius: 9999px;
  color: white;
}

.workflow-icon-post {
  background: linear-gradient(135deg, #2563eb, #4338ca);
  font-size: 2rem;
}

.workflow-icon-browse {
  background: linear-gradient(135deg, #60a5fa, #5b21b6);
}

.workflow-icon-browse :deep(svg) {
  width: 1.8rem;
  height: 1.8rem;
}

.workflow-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem 2rem;
}

.workflow-column {
  display: grid;
  gap: 1.5rem;
}

.step-card {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1.25rem;
  align-items: center;
  padding: 1.9rem;
}

.step-card h4 {
  margin: 0 0 0.35rem;
  font-size: 1.75rem;
}

.step-badge {
  display: grid;
  height: 4rem;
  width: 4rem;
  place-items: center;
  border-radius: 9999px;
  font-size: 1.45rem;
  font-weight: 900;
  color: white;
}

.step-badge-post {
  background: linear-gradient(135deg, #2563eb, #4338ca);
}

.step-badge-browse {
  background: linear-gradient(135deg, #60a5fa, #5b21b6);
}

.text-shell {
  max-width: 68rem;
}

.long-copy {
  display: grid;
  gap: 1.7rem;
}

.long-copy p {
  font-size: 1.12rem;
  line-height: 1.75;
  color: #334155;
}

.contact-card {
  display: grid;
  justify-items: center;
  gap: 1.25rem;
  padding: 3rem 2rem;
  text-align: center;
}

.contact-icon {
  display: grid;
  height: 6.5rem;
  width: 6.5rem;
  place-items: center;
  border-radius: 9999px;
  background: #eef2ff;
  color: #4338ca;
}

.contact-icon :deep(svg) {
  width: 2.2rem;
  height: 2.2rem;
}

.contact-email {
  font-size: 1.35rem;
  color: #0f172a;
}

.contact-email a {
  color: #4338ca;
  text-decoration: none;
}

.contact-actions {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
}

.about-footer {
  padding: 2rem 0 3.5rem;
}

.footer-inner {
  display: grid;
  justify-items: center;
  gap: 1.75rem;
  text-align: center;
}

.social-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.85rem;
}

.social-link {
  display: grid;
  height: 3rem;
  width: 3rem;
  place-items: center;
  border-radius: 0.9rem;
  background: #eef2ff;
  color: #4338ca;
}

.social-link :deep(svg) {
  width: 1.2rem;
  height: 1.2rem;
}

.cities {
  display: grid;
  gap: 0.6rem;
}

.cities h4 {
  margin: 0;
  font-size: 1.5rem;
}

.cities p {
  max-width: 60rem;
}

.footer-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.85rem 1.25rem;
}

.footer-links a {
  color: #64748b;
  text-decoration: none;
}

@media (max-width: 1024px) {
  .feature-grid-3,
  .feature-grid-2,
  .workflow-grid,
  .workflow-head {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .about-shell {
    padding: 0 1rem;
  }

  .hero-inner {
    padding-top: 4.5rem;
    padding-bottom: 4.5rem;
  }

  .hero-actions {
    width: 100%;
  }

  .hero-button {
    width: 100%;
  }

  .about-section {
    padding: 4rem 0;
  }

  .info-card {
    min-height: auto;
    padding: 1.4rem;
  }

  .info-card h3,
  .workflow-label h3 {
    font-size: 1.7rem;
  }

  .step-card {
    padding: 1.4rem;
  }

  .step-card h4 {
    font-size: 1.35rem;
  }

  .contact-email {
    font-size: 1.1rem;
  }
}
</style>
